import { groupPosition, type GroupAnalysis, type RiskLevel, type TokenMaturity, type WalletGroup } from '@pleco/shared';
import { fmtPct } from './fmt.js';

/**
 * Risk from trading behaviour, which the holder snapshot alone cannot see:
 * a launch bundle that already dumped, or bot farms inflating the holder
 * count, can make a token look "decentralised" right now.
 */
export interface TradingRiskFlag {
  level: RiskLevel;
  title: string;
  detail: string;
  groupId?: string;
}

const ORDER: RiskLevel[] = ['low', 'medium', 'high', 'critical'];
export const maxLevel = (...ls: RiskLevel[]): RiskLevel => ls.reduce((a, b) => (ORDER.indexOf(b) > ORDER.indexOf(a) ? b : a), 'low');
const capLevel = (l: RiskLevel, max: RiskLevel): RiskLevel => (ORDER.indexOf(l) > ORDER.indexOf(max) ? max : l);
const sol = (x: number) => (x >= 100 ? Math.round(x).toLocaleString('en-US') : x >= 10 ? x.toFixed(1) : x.toFixed(2));

/**
 * One sentence on where a group's position went since its peak. Phrased
 * from how the tokens left (sold vs transferred), never as "still holds 0%".
 */
export function positionSentence(g: WalletGroup, subject: string): string {
  const p = groupPosition(g);
  const peak = `${subject} controlled up to ${fmtPct(p.peak)} of the supply`;
  switch (p.state) {
    case 'sold':
      return `${peak} and have since sold the entire position${p.netSol >= 1 ? `, netting about ${sol(p.netSol)} SOL` : ''}.`;
    case 'moved':
      return `${peak}. Those wallets are now empty, but the tokens were transferred out rather than sold, so that supply may still be in the same hands.`;
    case 'mixed':
      return `${peak}. Those wallets are now empty: about ${Math.round(p.soldShare * 100)}% was sold on the market and the rest transferred to other wallets.`;
    case 'reduced':
      return `${peak} and have since sold or moved most of it; ${fmtPct(p.held)} is still in those wallets.`;
    default:
      return `${peak} and still hold ${fmtPct(p.held)}.`;
  }
}

/** "Sold 40% of its peak position in the last 30 min" → a sentence about the group. */
function sellingSentence(g: WalletGroup): string {
  const w = (g.exitWarning ?? '').replace(/^Sold/, 'sold').replace(/ min$/, ' minutes');
  const who = g.kind === 'dev_network' ? "The creator's wallet network" : g.name;
  return `${who} ${w}.`;
}

export function tradingRisk(a: GroupAnalysis | undefined, maturity: TokenMaturity = 'new'): { level: RiskLevel; flags: TradingRiskFlag[] } {
  if (!a) return { level: 'low', flags: [] };
  const flags: TradingRiskFlag[] = [];
  const established = maturity === 'established';
  const launchGroups = a.groups.filter((g) => g.kind === 'dev_network' || (g.kind === 'bundle' && g.name.startsWith('Launch')));
  for (const g of launchGroups) {
    if (g.stats.peakPercent < 15) continue;
    // Months later, a launch bundle only matters if it still holds a real share.
    if (established && g.stats.holdingPercent < 5) continue;
    const pos = groupPosition(g);
    let level: RiskLevel = g.stats.peakPercent >= 50 ? 'critical' : g.stats.peakPercent >= 25 ? 'high' : 'medium';
    // Already sold on the market: the dump has happened, nothing is left to sell.
    if (pos.state === 'sold') level = capLevel(level, 'high');
    flags.push({
      level,
      title: 'Bundled launch',
      detail: positionSentence(g, `${g.wallets.length} linked wallets`).replace(' of the supply', ' of the supply shortly after launch'),
      groupId: g.id,
    });
  }
  const held = a.groups.reduce((s, g) => s + g.stats.holdingPercent, 0);
  if (held >= 10) {
    flags.push(
      established
        ? {
            level: 'medium',
            title: 'Big linked holders',
            detail: `Linked wallets hold ${fmtPct(held)} of the supply. On an established token these are usually team, treasury or vesting wallets; the signal to watch is them moving tokens to exchanges or selling.`,
          }
        : {
            level: held >= 25 ? 'critical' : held >= 15 ? 'high' : 'medium',
            title: 'Linked wallets hold supply',
            detail: `Wallets that are linked to each other hold ${fmtPct(held)} of the supply and could sell at the same time.`,
          },
    );
  }
  // On an established token only a group with a real share can move the price.
  for (const g of a.groups.filter((x) => x.exitWarning && (!established || x.stats.holdingPercent >= 1 || x.stats.peakPercent >= 2))) {
    flags.push({ level: 'high', title: 'Group is selling', detail: sellingSentence(g), groupId: g.id });
  }
  const farmed = Object.values(a.wallets).filter((w) => w.tags.includes('farm_wallet') || w.tags.includes('gas_sponsored')).length;
  if (farmed >= 30) {
    flags.push({
      level: farmed >= 150 ? 'high' : 'medium',
      title: 'Farmed traders',
      detail: `${farmed} trading wallets were created in bursts and funded from a handful of sources, which inflates the holder count and trading volume.`,
    });
  }
  const dev = a.launch?.dev ? a.wallets[a.launch.dev] : undefined;
  if (dev?.tags.includes('exited') && maturity === 'new')
    flags.push({ level: 'medium', title: 'Dev sold', detail: "The creator's wallet has sold at least 90% of the tokens it bought." });
  flags.sort((x, y) => ORDER.indexOf(y.level) - ORDER.indexOf(x.level));
  return { level: maxLevel(...flags.map((f) => f.level)), flags };
}
